import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getRealtimeSteamingKeyMessageTopicList,
  getRealtimeSteamingSummaryTopicList,
} from '@op/services';
import { useEffect, useMemo, useState } from 'react';
import { KeyMessageTopicList, SummaryTopicList } from '../type';
import { useTablePropsInfo } from '@op/hooks/common';

export function useRealTimeStreamingSummary({ filter }: { filter: any }) {
  const [topicList, setTopicList] = useState<{ [key: string]: string }>({});
  const [list, setList] = useState<Array<SummaryTopicList>>([]);

  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setList([]);
      setTopicList([]);
    },
  });

  function request(defaultLoading?: boolean) {
    const { live_id, start_time, end_time } = filter;
    wrapperCancelRequest(
      getRealtimeSteamingSummaryTopicList,
      {
        live_id,
        start_time,
        end_time,
      },
      defaultLoading,
    )
      .then((data: any) => {
        const { list, topic_list } = data || {};
        // TODO: 兼容之前数据返回的格式
        const formatList = list.map((val) => ({
          ...val,
          topics: Array.isArray(val?.topics)
            ? topic_list.reduce((acc, key) => {
                acc[key] = 'neutral'; // default value
                return acc;
              }, {})
            : val?.topics,
        }));
        setList(formatList || []);

        if (Array.isArray(topic_list)) {
          const obj = topic_list.reduce((acc, key) => {
            acc[key] = 'neutral'; // default value
            return acc;
          }, {});
          setTopicList(obj);
        } else {
          setTopicList(topic_list || {});
        }
      })
      .catch(() => {});
  }

  useEffect(() => {
    if (filter?.live_id) {
      request(true);
    }
  }, [filter]);

  return { loading, topicList, list };
}

export function useRealTimeStreamingKeyMessage({ filter }: { filter: any }) {
  const { wrapperCancelRequest, loading } = useCancelRequestHook();
  const {
    pageInfo,
    total,
    setTotal,
    data,
    setData,
    nextPage,
    changePageInfoNoRender,
  } = useTablePropsInfo<SummaryTopicList>({
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  function request() {
    const { live_id, start_time, end_time } = filter;
    wrapperCancelRequest(getRealtimeSteamingKeyMessageTopicList, {
      live_id,
      start_time,
      end_time,
      ...pageInfo,
    })
      .then((dataInfo: { list: KeyMessageTopicList[]; total: number }) => {
        const { list, total } = dataInfo;

        const result: SummaryTopicList[] = [];

        (list || []).forEach(({ start_time, end_time, message_list }) => {
          result.push(
            ...(message_list || []).map(
              ({
                topic,
                key_message,
                key_message_zh,
                key_message_en,
                sentiment,
              }) => {
                return {
                  start_time,
                  end_time,
                  topics: {
                    [topic]: sentiment || 'neutral',
                  },
                  sentiment,
                  summary: key_message,
                  summary_zh: key_message_zh,
                  summary_en: key_message_en,
                };
              },
            ),
          );
        });

        setData([...data, ...result]);
        setTotal(total);
      })
      .catch(() => {
        const page = pageInfo.page - 1;
        if(page > 0){
          changePageInfoNoRender({ ...pageInfo, page });
        }
      });
  }

  useEffect(() => {
    if (filter?.live_id) {
      request();
    }
  }, [filter, pageInfo]);

  const isNoMore = useMemo(() => {
    return pageInfo.page * pageInfo.page_size >= total;
  }, [total, pageInfo]);

  return { loading, data, isNoMore, nextPage, total };
}
